import { API_ERROR_CODES, type ApiErrorBody, type ApiErrorDetail } from './api-contract';

/**
 * Error de API normalizado.
 *
 * Toda falla -HTTP, red, respuesta malformada- llega a la interfaz como una
 * instancia de esta clase. Asi los componentes manejan un unico tipo en lugar
 * de distinguir entre `Response`, `TypeError` de fetch y cuerpos inesperados.
 */
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly details: readonly ApiErrorDetail[];
  public readonly requestId: string;

  public constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.statusCode = body.statusCode;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.requestId;
  }

  /** La sesion expiro o no existe: procede reautenticar. */
  public get isUnauthenticated(): boolean {
    return this.statusCode === 401 || this.code === API_ERROR_CODES.unauthenticated;
  }

  /** Autenticado pero sin permiso: reintentar no sirve de nada. */
  public get isForbidden(): boolean {
    return this.statusCode === 403;
  }

  /** Error de validacion: los detalles se proyectan sobre el formulario. */
  public get isValidation(): boolean {
    return this.code === API_ERROR_CODES.validation || this.statusCode === 422;
  }

  /**
   * Fallo transitorio por el que merece la pena reintentar automaticamente.
   * Un 422 no entra: la peticion es invalida y reintentarla dara lo mismo.
   */
  public get isRetryable(): boolean {
    return (
      this.statusCode >= 500 ||
      this.statusCode === 429 ||
      this.code === API_ERROR_CODES.concurrentModification
    );
  }

  /** Mapa campo -> mensaje, listo para React Hook Form. */
  public get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const detail of this.details) {
      if (detail.field !== undefined) result[detail.field] = detail.message;
    }
    return result;
  }

  /**
   * Construye un ApiError cuando la peticion ni siquiera llego a completarse
   * (servidor caido, DNS, sin conexion). Se marca como 0 para distinguirlo de
   * un error devuelto por el servidor.
   */
  public static fromNetworkFailure(cause: unknown): ApiError {
    const error = new ApiError({
      statusCode: 0,
      code: 'NETWORK_ERROR',
      message: 'No se pudo contactar con el servidor. Revisar la conexion.',
      details: [],
      requestId: 'local',
      timestamp: new Date().toISOString(),
      path: '',
    });

    // Se conserva la causa original para diagnostico sin exponerla en la UI.
    if (cause instanceof Error) error.cause = cause;
    return error;
  }

  /** Respuesta con un cuerpo que no cumple el contrato. */
  public static fromMalformedResponse(status: number, path: string): ApiError {
    return new ApiError({
      statusCode: status,
      code: 'MALFORMED_RESPONSE',
      message: 'El servidor devolvio una respuesta inesperada.',
      details: [],
      requestId: 'local',
      timestamp: new Date().toISOString(),
      path,
    });
  }
}

/** Verifica que un cuerpo desconocido cumple el contrato de error. */
export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<ApiErrorBody>;
  return (
    typeof candidate.statusCode === 'number' &&
    typeof candidate.code === 'string' &&
    typeof candidate.message === 'string'
  );
}
